import React, { useState, useEffect } from "react";
import * as Sentry from '@sentry/browser';
import Api from "../../Api";
import classes from "./styles.module.css";

/**
 * TerminalSettlementTab — вкладка «Расчёты» в детальном виде терминала.
 * Settlement config + последние settlement батчи.
 *
 * Props:
 *   token (string)
 *   terminal (object) — { id, ... }
 */
function TerminalSettlementTab(props) {
    var token = props.token;
    var terminal = props.terminal;

    // Settlement config
    var _config = useState({
        enabled: false,
        commissionTransactionEnabled: false,
    });
    var config = _config[0];
    var setConfig = _config[1];

    var _configLoading = useState(true);
    var configLoading = _configLoading[0];
    var setConfigLoading = _configLoading[1];

    var _configSaving = useState(false);
    var configSaving = _configSaving[0];
    var setConfigSaving = _configSaving[1];

    var _configStatus = useState(null);
    var configStatus = _configStatus[0];
    var setConfigStatus = _configStatus[1];

    // Settlements list
    var _settlements = useState([]);
    var settlements = _settlements[0];
    var setSettlements = _settlements[1];

    var _settlementsLoading = useState(true);
    var settlementsLoading = _settlementsLoading[0];
    var setSettlementsLoading = _settlementsLoading[1];

    // Load config from balances endpoint
    useEffect(function () {
        if (!terminal || !terminal.id) return;
        setConfigLoading(true);
        setConfigStatus(null);

        var api = new Api({ token: token });
        api.getTerminalBalances(terminal.id).then(function (data) {
            var sc = (data && data.settlementConfig) || {};
            setConfig({
                enabled: !!sc.enabled,
                commissionTransactionEnabled: !!sc.commissionTransactionEnabled,
            });
            setConfigLoading(false);
        }).catch(function (err) {
            Sentry.captureException(err);
            setConfigLoading(false);
        });
    }, [terminal && terminal.id, token]);

    // Load settlements
    useEffect(function () {
        if (!terminal || !terminal.id) return;
        setSettlementsLoading(true);

        var api = new Api({ token: token });
        api.getSettlements({ terminal_id: terminal.id, limit: 10, page: 1 }).then(function (data) {
            setSettlements(Array.isArray(data.data) ? data.data : []);
            setSettlementsLoading(false);
        }).catch(function (err) {
            Sentry.captureException(err);
            setSettlementsLoading(false);
        });
    }, [terminal && terminal.id, token]);

    function onConfigChange(field, value) {
        var next = {};
        for (var k in config) { next[k] = config[k]; }
        next[field] = value;
        setConfig(next);
    }

    function onSaveConfig() {
        setConfigSaving(true);
        setConfigStatus(null);

        var api = new Api({ token: token });
        api.updateTerminalSettlementConfig(terminal.id, config).then(function () {
            setConfigSaving(false);
            setConfigStatus({ type: "success", text: "Настройки сохранены" });
        }).catch(function (err) {
            Sentry.captureException(err);
            setConfigSaving(false);
            var msg = (err && err.response && err.response.data && err.response.data.message) || "Ошибка сохранения";
            setConfigStatus({ type: "error", text: msg });
        });
    }

    function formatDate(dateStr) {
        if (!dateStr) return "\u2014";
        try {
            var d = new Date(dateStr);
            return d.toLocaleDateString("ru-RU") + " " + d.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
        } catch (e) {
            return dateStr;
        }
    }

    function formatAmount(kopecks) {
        if (kopecks == null) return "\u2014";
        return (Number(kopecks) / 100).toLocaleString("ru-RU", { minimumFractionDigits: 2 }) + " \u20BD";
    }

    function getStatusLabel(st) {
        if (st === 1) return React.createElement("span", { className: classes.StatusBadge + " " + classes.StatusConfirmed }, "Подтверждён");
        if (st === 2) return React.createElement("span", { className: classes.StatusBadge + " " + classes.StatusCancelled }, "Отменён");
        return React.createElement("span", { className: classes.StatusBadge }, "Создан");
    }

    if (configLoading) {
        return React.createElement("div", { className: classes.Container }, "Загрузка...");
    }

    return React.createElement("div", { className: classes.Container },
        // Settlement Config
        React.createElement("div", { className: classes.Title }, "Настройки расчётов"),
        React.createElement("div", { className: classes.ConfigSection },
            React.createElement("div", { className: classes.ToggleRow },
                React.createElement("input", {
                    type: "checkbox",
                    checked: config.enabled,
                    onChange: function (e) { onConfigChange("enabled", e.target.checked); },
                }),
                React.createElement("span", { className: classes.ToggleLabel }, "Settlement включён")
            ),
            React.createElement("div", { className: classes.ToggleRow },
                React.createElement("input", {
                    type: "checkbox",
                    checked: config.commissionTransactionEnabled,
                    onChange: function (e) { onConfigChange("commissionTransactionEnabled", e.target.checked); },
                }),
                React.createElement("span", { className: classes.ToggleLabel }, "Создавать транзакции комиссии")
            ),
            React.createElement("button", {
                className: classes.SaveBtn,
                onClick: onSaveConfig,
                disabled: configSaving,
            }, configSaving ? "Сохранение..." : "Сохранить настройки"),
            configStatus && React.createElement("div", {
                className: classes.StatusMsg + " " + (configStatus.type === "success" ? classes.StatusSuccess : classes.StatusError),
            }, configStatus.text)
        ),

        // Recent Settlements
        React.createElement("hr", { className: classes.Divider }),
        React.createElement("div", { className: classes.SettlementsTitle }, "Последние расчёты"),

        settlementsLoading
            ? React.createElement("div", null, "Загрузка...")
            : settlements.length === 0
                ? React.createElement("div", { className: classes.EmptyText }, "Нет расчётов для этого терминала")
                : React.createElement("div", { className: classes.TableScroll },
                    React.createElement("table", { className: classes.Table },
                        React.createElement("thead", null,
                            React.createElement("tr", null,
                                React.createElement("th", null, "ID"),
                                React.createElement("th", null, "Дата"),
                                React.createElement("th", null, "Сумма"),
                                React.createElement("th", null, "Транзакций"),
                                React.createElement("th", null, "Статус"),
                                React.createElement("th", null, "Комментарий")
                            )
                        ),
                        React.createElement("tbody", null,
                            settlements.map(function (s) {
                                return React.createElement("tr", { key: s.id },
                                    React.createElement("td", null, s.id),
                                    React.createElement("td", null, formatDate(s.created_at)),
                                    React.createElement("td", null, formatAmount(s.amount)),
                                    React.createElement("td", null, s.transactions_count),
                                    React.createElement("td", null, getStatusLabel(s.status)),
                                    React.createElement("td", null, s.comment || "\u2014")
                                );
                            })
                        )
                    )
                ),

        React.createElement("div", { style: { marginTop: "15px", fontSize: "12px", color: "#888" } },
            "Полное управление расчётами доступно в разделе ",
            React.createElement("strong", null, "Расчёты"),
            " в меню слева."
        )
    );
}

export default TerminalSettlementTab;



// WEBPACK FOOTER //
// ./src/Components/TerminalSettlementTab/index.js